import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { T } from '@tolgee/react';
import { useProducts, useCategories } from '../../../lib/api/hooks.tsx';
import { Product } from '../../../lib/api/models';
import { useZarazTracking } from '../../../lib/zaraz/useZarazTracking';
import api from '../../../lib/api';
import Breadcrumbs from '../components/Breadcrumbs';
import Pagination from '../components/Pagination';
import ProductCard from '../components/ProductCard';

const CategoryDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [category, setCategory] = useState<any | null>(null);
  const [children, setChildren] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>(slug || 'all');
  const [sortType, setSortType] = useState<string>('featured');
  const [categoryLoading, setCategoryLoading] = useState<boolean>(true);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const headerRef = useRef(null);

  // Hook de Zaraz tracking
  const { trackProductListViewed, trackProductClicked } = useZarazTracking();

  // Usar hooks de la nueva API
  const { data: categories } = useCategories();

  // Memoizar las opciones para evitar recrearlas en cada render
  const productOptions = useMemo(() => {
    const options: any = {
      page: currentPage,
      limit: 25
    };
    if (slug) {
      options.filter = { category: slug };
    }
    return options;
  }, [slug, currentPage]);

  const { data: products, loading, error, refetch, pagination } = useProducts(productOptions);

  // Cargar información de la categoría
  useEffect(() => {
    const fetchCategory = async () => {
      if (!slug) return;
      setCategoryLoading(true);
      setCategoryError(null);
      try {
        const res = await api.categories.getCategories();
        if (res.success && res.data) {
          const list = res.data as any[];
          const cat = list.find(c => c.slug === slug);
          setCategory(cat || null);
          if (cat) {
            const childRes = await api.categories.getChildren(cat.id);
            setChildren(childRes.success && childRes.data ? (childRes.data as any[]) : []);
          } else {
            setChildren([]);
          }
        } else {
          setCategoryError(res.error?.message || 'No se pudo cargar la categoría');
        }
      } catch (e: any) {
        setCategoryError(e.message || 'Error desconocido');
      } finally {
        setCategoryLoading(false);
      }
    };
    fetchCategory();
  }, [slug]);

  // Actualizar productos filtrados cuando cambian los productos originales
  useEffect(() => {
    if (products) {
      setFilteredProducts(products);
      // Trackear vista de lista de productos
      trackProductListViewed(products, `Categoría: ${category?.name || slug}`);
    }
  }, [products, category, slug, trackProductListViewed]);

  // Actualizar filtro activo cuando cambia el slug
  useEffect(() => {
    setActiveFilter(slug || 'all');
  }, [slug]);

  // Manejar cambio de página
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll al inicio de la página
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Función para filtrar productos (navegación entre categorías)
  const filterProducts = (filter: string) => {
    setActiveFilter(filter);
    if (filter === 'all') {
      navigate('/products');
    } else {
      navigate(`/categories/${filter}`);
    }
  };

  // Función para ordenar productos
  const sortProducts = (sortType: string) => {
    setSortType(sortType);

    let sorted = [...filteredProducts];

    switch (sortType) {
      case 'price-asc':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        sorted.sort((a, b) => b.price - a.price);
        break;
      case 'name-asc':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        sorted.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        // No ordenar para 'featured'
        break;
    }

    setFilteredProducts(sorted);
  };

  // Variantes para animaciones
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  // Mensaje de error
  if (categoryError || error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-8 max-w-md mx-auto">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            <T keyName="category.error_loading">Error al cargar la categoría</T>
          </h2>
          <p className="text-gray-600 mb-6">{categoryError || error}</p>
          <button
            onClick={() => {
              refetch();
              navigate('/products');
            }}
            className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-colors"
          >
            <T keyName="category.view_all_products">Ver todos los productos</T>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      {/* SEO dinámico para categoría */}
      <script dangerouslySetInnerHTML={{ __html: `
        (function(){
          var t = document.querySelector('title');
          if(t) t.textContent = '${category?.name || slug} | Jarad Pet Food';
          var d = document.querySelector('meta[name="description"]');
          if(d) d.setAttribute('content', '${category?.description || `Explora productos de ${category?.name || slug}`}');
          // BreadcrumbList schema
          var ld = {
            "@context":"https://schema.org",
            "@type":"BreadcrumbList",
            "itemListElement":[
              {"@type":"ListItem","position":1,"name":"Inicio","item":"https://jaradpetfood.com/"},
              {"@type":"ListItem","position":2,"name":"Productos","item":"https://jaradpetfood.com/products"},
              {"@type":"ListItem","position":3,"name":"${category?.name || slug}","item":"https://jaradpetfood.com/categories/${slug}"}
            ]
          };
          var n = document.createElement('script');
          n.type='application/ld+json';
          n.text=JSON.stringify(ld);
          document.head.appendChild(n);
        })();
      `}} />

      {/* Header compacto y profesional */}
      <div
        ref={headerRef}
        className="border-b border-gray-200 bg-white"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {category && (
            <div className="mb-3">
              <Breadcrumbs categoryId={category.id} />
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              {category?.name && category.name.includes('>') ? (
                <>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">
                    {category.name.split('>')[0].trim()}
                  </p>
                  <h1 className="text-2xl font-bold text-gray-900 mb-1">
                    {category.name.split('>').pop()!.trim()}
                  </h1>
                </>
              ) : (
                <h1 className="text-2xl font-bold text-gray-900 mb-1">
                  {category?.name || (slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : 'Categoría')}
                </h1>
              )}
              {category?.description && (
                <p className="text-sm text-gray-600 mb-2 max-w-3xl">
                  {category.description}
                </p>
              )}
              <p className="text-sm text-gray-500">
                {loading ? <T keyName="common.loading">Cargando...</T> : (
                  <>{filteredProducts.length} {filteredProducts.length === 1 ? 'resultado' : 'resultados'}</>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Subcategorías si existen */}
      {children.length > 0 && (
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              <T keyName="category.subcategories">Subcategorías</T>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {children.map((child) => (
                <button
                  key={child.id}
                  onClick={() => navigate(`/categories/${child.slug}`)}
                  className="block p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md hover:border-black transition-all text-left"
                >
                  <div className="font-medium text-gray-900">{child.name}</div>
                  {child.description && (
                    <div className="text-sm text-gray-500 mt-1 line-clamp-2">{child.description}</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Layout con Sidebar y Productos */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar de Filtros - Fijo en desktop */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="lg:sticky lg:top-24 space-y-6">
              {/* Categorías */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  <T keyName="sidebar.categories">Categorías</T>
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => filterProducts('all')}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      activeFilter === 'all'
                        ? 'bg-black text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <T keyName="sidebar.view_all_products">Todos los productos</T>
                  </button>

                  {categories && categories.length > 0 ? (
                    categories.map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => filterProducts(cat.slug)}
                        className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                          activeFilter === cat.slug
                            ? 'bg-black text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))
                  ) : (
                    ['aves', 'roedores', 'perros', 'gatos'].map(cat => (
                      <button
                        key={cat}
                        onClick={() => filterProducts(cat)}
                        className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                          activeFilter === cat
                            ? 'bg-black text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Ordenar por */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  <T keyName="category.sort_by">Ordenar por</T>
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => sortProducts('featured')}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      sortType === 'featured'
                        ? 'bg-black text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <T keyName="category.featured">Destacados</T>
                  </button>
                  <button
                    onClick={() => sortProducts('price-asc')}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      sortType === 'price-asc'
                        ? 'bg-black text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <T keyName="category.price_low_high">Precio: Menor a mayor</T>
                  </button>
                  <button
                    onClick={() => sortProducts('price-desc')}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      sortType === 'price-desc'
                        ? 'bg-black text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <T keyName="category.price_high_low">Precio: Mayor a menor</T>
                  </button>
                  <button
                    onClick={() => sortProducts('name-asc')}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      sortType === 'name-asc'
                        ? 'bg-black text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <T keyName="category.name_a_z">Nombre: A-Z</T>
                  </button>
                  <button
                    onClick={() => sortProducts('name-desc')}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      sortType === 'name-desc'
                        ? 'bg-black text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <T keyName="category.name_z_a">Nombre: Z-A</T>
                  </button>
                </div>
              </div>
            </div>
          </aside>

          {/* Área de Productos */}
          <main className="flex-1 min-w-0">
            {loading || categoryLoading ? (
              // Skeleton loader para productos
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {Array.from({ length: 9 }).map((_, index) => (
                  <div key={index} className="animate-pulse">
                    <div className="bg-gray-200 rounded-lg aspect-square mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              // Mensaje de no hay productos
              <div className="text-center py-16">
                <h3 className="text-2xl font-medium text-gray-900 mb-4">
                  <T keyName="category.no_products_found">No se encontraron productos</T>
                </h3>
                <p className="text-gray-500 mb-8">
                  <T keyName="category.no_products_message">Esta categoría aún no tiene productos disponibles</T>
                </p>
                <button
                  onClick={() => navigate('/products')}
                  className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-colors"
                >
                  <T keyName="category.view_all_products">Ver todos los productos</T>
                </button>
              </div>
            ) : (
              <>
                {/* Grid de productos */}
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6"
                >
                  {filteredProducts.map((product, index) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      index={index}
                      onProductClick={trackProductClicked}
                    />
                  ))}
                </motion.div>

                {/* Paginación */}
                {pagination && pagination.pages > 1 ? (
                  <div className="mt-12">
                    <Pagination
                      currentPage={pagination.page}
                      totalPages={pagination.pages}
                      onPageChange={handlePageChange}
                      totalItems={pagination.total}
                      itemsPerPage={pagination.limit}
                    />
                  </div>
                ) : null}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default CategoryDetailPage;
